/**
 * Authentication + KEK release state machine.
 *
 * Per Tech Spec §6.2.3 + PFO §2.2 + scope memo §5.1:
 *
 *   Patient Auth Factor → PMK (derived in client memory)
 *                              ↓ unwrap
 *                            KEK (loaded into ActiveSession)
 *                              ↓ unwrap (per record)
 *                       Per-Record DEK
 *                              ↓ decrypt
 *                       Cleartext projection record
 *
 * The flow walks the patient through:
 *
 *   1. Intro — sign-in prompt with knowledge + possession factor
 *      framing (no auto-fill of stored credentials)
 *   2. Knowledge factor — password / passphrase (PFO §2.2 knowledge
 *      factor)
 *   3. Possession factor — verification code from SHT-issued
 *      possession factor (phone / hardware token / device-bound
 *      credential per PFO §2.2). v0 stub accepts a non-empty code
 *      string; production wiring connects to SMS / TOTP / WebAuthn
 *      surface (hire-gated production integration).
 *   4. Deriving PMK — PBKDF2 in flight; cleartext password bytes
 *      live transiently in the encoder buffer + zeroized after
 *      derivation
 *   5. Fetching wrapped KEK — `IPatientKeyCustodian.fetchWrappedKek`
 *   6. Unwrapping KEK — `IEnvelopeCryptoAdapter.unwrapKey` produces
 *      a non-extractable CryptoKey
 *   7. Session active — KEK loaded into `ActiveSession`; patient
 *      session is alive
 *   8. Sign-out — patient initiates; session ends; KEK reference
 *      dropped
 *
 * **Doctrinal load-bearing transitions (encoded structurally):**
 *
 *   - **Both factors required.** Per PFO §2.2 standard PHG access:
 *     "Knowledge + possession." The state machine cannot reach
 *     `deriving-pmk` without affirmative submission of both
 *     factors. No skip-possession-factor branch.
 *   - **PMK never persists.** PMK is derived non-extractable; the
 *     adapter's `derivePMK` produces a CryptoKey the runtime
 *     refuses to surface bytes for. After `kek-unwrapped`, the PMK
 *     reference is dropped — only the KEK reference is retained
 *     in `ActiveSession`. The state machine encodes this by NOT
 *     carrying a PMK field beyond the `unwrapping-kek` state.
 *   - **Sign-out is patient-affirmative.** The flow has no idle
 *     timeout at v0 (PFO §2.2 12-24h configurable lands at
 *     refinement); patient explicitly initiates sign-out via
 *     dispatched event. The state machine encodes session-active
 *     as a sticky state until sign-out / error.
 *
 * **Risk-tiered step-up (PFO §2.2 table) — v1.5+.** v0 ships
 * standard PHG access (read-only) flow only. Sensitive-scope
 * step-up (delegation, emergency revocation, trusted-other
 * enrollment, sensitive-category Consent Artifact issuance — all
 * require fresh MFA + 3 factors per PFO §2.2 table) lands as a
 * refinement that adds inherence-factor / additional-possession-
 * factor branches to the state machine. v0 ships the structural
 * 2-factor surface; refinement extends without breaking changes.
 *
 * **Patient-Facing Terms v1.3 doctrinal-copy gate** (coord issue
 * #42). UI copy is placeholder until v1.3 lands; views structured
 * for mechanical swap.
 */
export const AUTH_TERMINAL_STATES = new Set([
    'signed-out',
    'failed',
]);
export function isTerminal(state) {
    return AUTH_TERMINAL_STATES.has(state.kind);
}
// ---------- Transitions ----------
export class AuthTransitionError extends Error {
    constructor(currentKind, eventKind) {
        super(`invalid auth transition: cannot apply event "${eventKind}" in state "${currentKind}"`);
        this.name = 'AuthTransitionError';
    }
}
export function applyEvent(state, event) {
    // Universal `fail` from any non-terminal state.
    if (!isTerminal(state) && event.kind === 'fail') {
        return { kind: 'failed', code: event.code, message: event.message };
    }
    // `restart` from any terminal state returns to intro.
    if (isTerminal(state) && event.kind === 'restart') {
        return { kind: 'intro' };
    }
    // `sign-out` from session-active — patient-affirmative session
    // end.
    if (state.kind === 'session-active' && event.kind === 'sign-out') {
        return { kind: 'signed-out' };
    }
    switch (state.kind) {
        case 'intro':
            if (event.kind === 'begin-sign-in') {
                return { kind: 'collecting-knowledge-factor' };
            }
            break;
        case 'collecting-knowledge-factor':
            if (event.kind === 'submit-knowledge-factor') {
                if (event.knowledgeFactor.length === 0) {
                    throw new AuthTransitionError(state.kind, event.kind);
                }
                return {
                    kind: 'collecting-possession-factor',
                    knowledgeFactor: event.knowledgeFactor,
                };
            }
            break;
        case 'collecting-possession-factor':
            if (event.kind === 'submit-possession-factor') {
                if (event.possessionFactor.length === 0) {
                    throw new AuthTransitionError(state.kind, event.kind);
                }
                return { kind: 'deriving-pmk' };
            }
            break;
        case 'deriving-pmk':
            if (event.kind === 'pmk-derived') {
                return { kind: 'fetching-wrapped-kek' };
            }
            break;
        case 'fetching-wrapped-kek':
            if (event.kind === 'wrapped-kek-fetched') {
                return { kind: 'unwrapping-kek', wrappedMaterial: event.material };
            }
            break;
        case 'unwrapping-kek':
            if (event.kind === 'kek-unwrapped') {
                return { kind: 'session-active', handle: event.handle };
            }
            break;
        case 'session-active':
        case 'signed-out':
        case 'failed':
            break;
    }
    throw new AuthTransitionError(state.kind, event.kind);
}
export function initialAuthState() {
    return { kind: 'intro' };
}
//# sourceMappingURL=auth-state.js.map